import React, { useState, useMemo, useRef } from 'react';
import { ChevronDown, ChevronUp, BarChart2 } from 'react-feather';
import './TaskFeatureTimeline.css';

const TaskFeatureTimeline = ({ featureTags, tasks, onTaskClick, isCollapsed: controlledCollapsed, onToggleCollapse }) => {
    const [internalCollapsed, setInternalCollapsed] = useState(true);
    const isCollapsed = controlledCollapsed !== undefined ? controlledCollapsed : internalCollapsed;
    const setIsCollapsed = onToggleCollapse 
        ? (_value) => onToggleCollapse() 
        : (value) => setInternalCollapsed(value);
    const [timeRange, setTimeRange] = useState('30'); // '30', '60', '90', 'all'
    const [hoveredTask, setHoveredTask] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const svgRef = useRef(null);
    const timelineRef = useRef(null);

    // Status color mapping
    const statusColors = {
        'open': '#9ca3af',
        'in-progress': '#f59e0b',
        'completed': '#10b981',
        'released': '#3b82f6'
    };

    // Process and group tasks by feature
    const processedData = useMemo(() => {
        if (!tasks || tasks.length === 0) {
            return { features: [], untagged: [], timeRange: null };
        }

        // Filter tasks by time range
        const now = new Date();
        const filteredTasks = tasks.filter(task => {
            if (!task.created_at) return false;
            const taskDate = new Date(task.created_at);
            if (isNaN(taskDate.getTime())) return false;

            if (timeRange === 'all') return true;
            
            const daysAgo = parseInt(timeRange, 10);
            const cutoffDate = new Date(now);
            cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
            
            return taskDate >= cutoffDate;
        });

        if (filteredTasks.length === 0) {
            return { features: [], untagged: [], timeRange: null };
        }

        // Calculate time range bounds
        const dates = filteredTasks
            .map(t => new Date(t.created_at))
            .filter(d => !isNaN(d.getTime()));
        
        if (dates.length === 0) {
            return { features: [], untagged: [], timeRange: null };
        }

        const minDate = new Date(Math.min(...dates));
        const maxDate = new Date(Math.max(...dates));
        
        // Add some padding (5% on each side)
        const timeSpan = maxDate - minDate;
        const padding = timeSpan * 0.05;
        const startDate = new Date(minDate.getTime() - padding);
        const endDate = new Date(maxDate.getTime() + padding);

        // Group tasks by feature
        const featureMap = new Map();
        const untaggedTasks = [];

        filteredTasks.forEach(task => {
            if (task.feature_tag?.id) {
                const featureId = task.feature_tag.id;
                if (!featureMap.has(featureId)) {
                    featureMap.set(featureId, {
                        ...task.feature_tag,
                        tasks: []
                    });
                }
                featureMap.get(featureId).tasks.push(task);
            } else {
                untaggedTasks.push(task);
            }
        });

        // Convert map to array and sort by task count (descending)
        const features = Array.from(featureMap.values())
            .map(feature => {
                // Sort tasks by creation date
                const sortedTasks = feature.tasks.sort((a, b) => 
                    new Date(a.created_at) - new Date(b.created_at)
                );
                
                // Calculate cumulative task count and loading velocity
                let cumulativeCount = 0;
                const tasksWithCumulative = sortedTasks.map((task, index) => {
                    cumulativeCount++;
                    const taskDate = new Date(task.created_at);
                    const prevTaskDate = index > 0 ? new Date(sortedTasks[index - 1].created_at) : startDate;
                    const daysBetween = (taskDate - prevTaskDate) / (1000 * 60 * 60 * 24);
                    const velocity = daysBetween > 0 ? 1 / daysBetween : 10; // tasks per day
                    
                    return {
                        ...task,
                        x: calculateXPosition(task.created_at, startDate, endDate),
                        y: 0, // Will be set based on lane position
                        cumulativeCount,
                        velocity,
                        daysSinceStart: (taskDate - startDate) / (1000 * 60 * 60 * 24)
                    };
                });
                
                // Calculate overall loading metrics
                const totalDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
                const avgVelocity = sortedTasks.length / Math.max(totalDays, 1);
                
                // Calculate recent velocity based on actual time span of recent tasks
                const recentTasks = sortedTasks.slice(-Math.min(5, sortedTasks.length));
                let recentVelocity = 0;
                if (recentTasks.length > 1) {
                    const firstRecentDate = new Date(recentTasks[0].created_at);
                    const lastRecentDate = new Date(recentTasks[recentTasks.length - 1].created_at);
                    const recentDays = Math.max((lastRecentDate - firstRecentDate) / (1000 * 60 * 60 * 24), 1);
                    recentVelocity = recentTasks.length / recentDays;
                } else if (recentTasks.length === 1) {
                    // For single task, use time since task creation
                    const taskDate = new Date(recentTasks[0].created_at);
                    const daysSinceTask = Math.max((endDate - taskDate) / (1000 * 60 * 60 * 24), 1);
                    recentVelocity = 1 / daysSinceTask;
                }
                
                return {
                    ...feature,
                    tasks: tasksWithCumulative,
                    maxCumulative: cumulativeCount,
                    avgVelocity,
                    recentVelocity,
                    loadingIntensity: recentVelocity > avgVelocity * 1.5 ? 'high' : 
                                    recentVelocity > avgVelocity * 0.5 ? 'medium' : 'low'
                };
            })
            .sort((a, b) => b.tasks.length - a.tasks.length);

        // Process untagged tasks
        const untagged = untaggedTasks.map(task => ({
            ...task,
            x: calculateXPosition(task.created_at, startDate, endDate),
            y: 0
        }));

        return {
            features,
            untagged,
            timeRange: {
                start: startDate,
                end: endDate,
                span: endDate - startDate
            }
        };
    }, [tasks, timeRange]);

    // Calculate X position for a task based on its creation date
    function calculateXPosition(createdAt, startDate, endDate) {
        const taskDate = new Date(createdAt);
        const timeSpan = endDate - startDate;
        const taskOffset = taskDate - startDate;
        return (taskOffset / timeSpan) * 100; // Percentage
    }

    // Format date for display
    const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Get time axis labels
    const timeAxisLabels = useMemo(() => {
        if (!processedData.timeRange) return [];
        
        const { start, end } = processedData.timeRange;
        const span = end - start;
        const days = span / (1000 * 60 * 60 * 24);
        
        // Determine label frequency based on time span - show more dates for accuracy
        let intervalDays;
        if (days <= 14) {
            intervalDays = 2; // Every 2 days
        } else if (days <= 30) {
            intervalDays = 3; // Every 3 days
        } else if (days <= 60) {
            intervalDays = 7; // Weekly
        } else if (days <= 90) {
            intervalDays = 10; // Every 10 days
        } else {
            intervalDays = 14; // Bi-weekly
        }

        const labels = [];
        let currentDate = new Date(start);
        while (currentDate <= end) {
            const x = calculateXPosition(currentDate, start, end);
            labels.push({
                date: new Date(currentDate),
                x,
                label: formatDate(currentDate)
            });
            currentDate = new Date(currentDate.getTime() + intervalDays * 24 * 60 * 60 * 1000);
        }

        return labels;
    }, [processedData.timeRange]);

    // Handle task marker hover
    const handleTaskHover = (task, event) => {
        setHoveredTask(task);
        if (timelineRef.current) {
            const rect = timelineRef.current.getBoundingClientRect();
            setTooltipPosition({
                x: event.clientX - rect.left,
                y: event.clientY - rect.top
            });
        }
    };

    const handleTaskLeave = () => {
        setHoveredTask(null);
    };

    const handleTaskClick = (task) => {
        if (onTaskClick) {
            onTaskClick(task);
        }
    };

    // Calculate summary statistics
    const summary = useMemo(() => {
        const totalTasks = processedData.features.reduce((sum, f) => sum + f.tasks.length, 0) + 
                          processedData.untagged.length;
        const featureCount = processedData.features.length;
        const untaggedCount = processedData.untagged.length;
        const spanDays = processedData.timeRange 
            ? Math.ceil((processedData.timeRange.end - processedData.timeRange.start) / (1000 * 60 * 60 * 24))
            : 0;

        return { totalTasks, featureCount, untaggedCount, spanDays };
    }, [processedData]);

    const laneHeight = 70;
    const timelinePadding = { left: 200, right: 20, top: 40, bottom: 60 };
    const timelineWidth = 1200;
    const timelineHeight = (processedData.features.length + (processedData.untagged.length > 0 ? 1 : 0)) * laneHeight + timelinePadding.top + timelinePadding.bottom;

    return (
        <div className="task-feature-timeline-section">
            {controlledCollapsed === undefined && (
                <div 
                    className="task-feature-timeline-header" 
                    onClick={() => setIsCollapsed(!isCollapsed)} 
                    style={{ cursor: 'pointer' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                        <h3 className="task-feature-timeline-title">
                            <BarChart2 size={16} />
                            Task-Feature Timeline
                        </h3>
                    </div>
                    <button 
                        className="stats-toggle-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsCollapsed(!isCollapsed);
                        }}
                        aria-label={isCollapsed ? "Expand timeline" : "Collapse timeline"}
                    >
                        {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                </div>
            )}

            <div className={`task-feature-timeline-content ${isCollapsed ? 'collapsed' : ''}`}>
                {/* Time Range Selector */}
                <div className="timeline-controls">
                    <div className="time-range-selector">
                        <span className="time-range-label">Time Range:</span>
                        {['30', '60', '90', 'all'].map(range => (
                            <button
                                key={range}
                                className={`time-range-btn ${timeRange === range ? 'active' : ''}`}
                                onClick={() => setTimeRange(range)}
                            >
                                {range === 'all' ? 'All' : `${range}d`}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Legend */}
                <div className="timeline-legend">
                    <div className="legend-group">
                        <span className="legend-label">Status:</span>
                        {Object.entries(statusColors).map(([status, color]) => (
                            <div key={status} className="legend-item">
                                <span 
                                    className="legend-dot" 
                                    style={{ backgroundColor: color }}
                                />
                                <span className="legend-text">
                                    {status.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className="legend-group">
                        <span className="legend-note">(Line shows task accumulation over time)</span>
                    </div>
                </div>

                {/* Timeline Visualization */}
                {processedData.features.length === 0 && processedData.untagged.length === 0 ? (
                    <div className="timeline-empty">
                        <p>No tasks found for the selected time range</p>
                    </div>
                ) : (
                    <div className="timeline-container" ref={timelineRef}>
                        <svg
                            ref={svgRef}
                            className="timeline-svg"
                            width="100%"
                            height={timelineHeight}
                            viewBox={`0 0 ${timelineWidth} ${timelineHeight}`}
                            preserveAspectRatio="xMidYMid meet"
                        >
                            <defs>
                                <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                                    <feDropShadow dx="0" dy="2" stdDeviation="2" floodOpacity="0.2"/>
                                </filter>
                                {/* Gradients for cumulative lines */}
                                {processedData.features.map((feature, idx) => (
                                    <linearGradient key={`gradient-${feature.id}`} id={`gradient-${feature.id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                                        <stop offset="0%" stopColor={feature.color || '#6366f1'} stopOpacity="0.3" />
                                        <stop offset="100%" stopColor={feature.color || '#6366f1'} stopOpacity="0.05" />
                                    </linearGradient>
                                ))}
                            </defs>

                            {/* Grid Lines */}
                            <g className="grid-lines">
                                {timeAxisLabels.map((label, index) => (
                                    <line
                                        key={`grid-${index}`}
                                        x1={timelinePadding.left + (label.x / 100) * (timelineWidth - timelinePadding.left - timelinePadding.right)}
                                        y1={timelinePadding.top}
                                        x2={timelinePadding.left + (label.x / 100) * (timelineWidth - timelinePadding.left - timelinePadding.right)}
                                        y2={timelineHeight - timelinePadding.bottom}
                                        stroke="#e5e7eb"
                                        strokeWidth="1"
                                        strokeDasharray="2,2"
                                    />
                                ))}
                            </g>

                            {/* Feature Lanes */}
                            <g className="feature-lanes">
                                {processedData.features.map((feature, featureIndex) => {
                                    const laneY = timelinePadding.top + (featureIndex * laneHeight) + (laneHeight / 2);
                                    
                                    return (
                                        <g key={feature.id} className="feature-lane">
                                            {/* Lane Background */}
                                            <rect
                                                x={timelinePadding.left}
                                                y={timelinePadding.top + (featureIndex * laneHeight)}
                                                width={timelineWidth - timelinePadding.left - timelinePadding.right}
                                                height={laneHeight}
                                                fill={featureIndex % 2 === 0 ? '#ffffff' : '#f9fafb'}
                                                className="lane-background"
                                            />
                                            
                                            {/* Lane Accent (Feature Color) with Loading Intensity Indicator */}
                                            <line
                                                x1={timelinePadding.left}
                                                y1={timelinePadding.top + (featureIndex * laneHeight)}
                                                x2={timelinePadding.left}
                                                y2={timelinePadding.top + (featureIndex * laneHeight) + laneHeight}
                                                stroke={feature.color || '#6366f1'}
                                                strokeWidth={feature.loadingIntensity === 'high' ? '6' : 
                                                           feature.loadingIntensity === 'medium' ? '4' : '3'}
                                                className="lane-accent"
                                                opacity={feature.loadingIntensity === 'high' ? '1' : 
                                                        feature.loadingIntensity === 'medium' ? '0.8' : '0.6'}
                                            />
                                            
                                            {/* Cumulative Task Count Area */}
                                            {feature.tasks.length > 0 && (
                                                <g className="cumulative-area">
                                                    {/* Area fill */}
                                                    <path
                                                        d={`M ${timelinePadding.left},${laneY + 20} ${
                                                            feature.tasks.map((task, taskIdx) => {
                                                                const x = timelinePadding.left + (task.x / 100) * (timelineWidth - timelinePadding.left - timelinePadding.right);
                                                                const cumulativeHeight = (task.cumulativeCount / feature.maxCumulative) * 30;
                                                                return `L ${x},${laneY + 20 - cumulativeHeight}`;
                                                            }).join(' ')
                                                        } L ${timelinePadding.left + (feature.tasks[feature.tasks.length - 1].x / 100) * (timelineWidth - timelinePadding.left - timelinePadding.right)},${laneY + 20} Z`}
                                                        fill={`url(#gradient-${feature.id})`}
                                                        opacity="0.4"
                                                    />
                                                    {/* Cumulative line */}
                                                    <polyline
                                                        points={feature.tasks.map(task => {
                                                            const x = timelinePadding.left + (task.x / 100) * (timelineWidth - timelinePadding.left - timelinePadding.right);
                                                            const cumulativeHeight = (task.cumulativeCount / feature.maxCumulative) * 30;
                                                            return `${x},${laneY + 20 - cumulativeHeight}`;
                                                        }).join(' ')}
                                                        fill="none"
                                                        stroke={feature.color || '#6366f1'}
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        opacity="0.7"
                                                        className="cumulative-line"
                                                    />
                                                </g>
                                            )}

                                            {/* Feature Label */}
                                            <g className="lane-label">
                                                <rect
                                                    x="0"
                                                    y={timelinePadding.top + (featureIndex * laneHeight)}
                                                    width={timelinePadding.left}
                                                    height={laneHeight}
                                                    fill="transparent"
                                                />
                                                {/* Feature name with colored background */}
                                                <rect
                                                    x={timelinePadding.left - 200}
                                                    y={laneY - 10}
                                                    width="160"
                                                    height="20"
                                                    rx="4"
                                                    fill={`${feature.color || '#6366f1'}15`}
                                                    stroke={feature.color || '#6366f1'}
                                                    strokeWidth="1"
                                                />
                                                <text
                                                    x={timelinePadding.left - 110}
                                                    y={laneY}
                                                    textAnchor="middle"
                                                    fontSize="11"
                                                    fill={feature.color || '#6366f1'}
                                                    fontWeight="500"
                                                    dominantBaseline="middle"
                                                >
                                                    {feature.name}
                                                </text>
                                                <text
                                                    x={timelinePadding.left - 20}
                                                    y={laneY}
                                                    textAnchor="end"
                                                    fontSize="11"
                                                    fill="#6b7280"
                                                    className="lane-task-count"
                                                    dominantBaseline="middle"
                                                >
                                                    ({feature.tasks.length})
                                                </text>
                                            </g>

                                            {/* Task Markers */}
                                            <g className="task-markers">
                                                {feature.tasks.map((task, taskIndex) => {
                                                    const x = timelinePadding.left + (task.x / 100) * (timelineWidth - timelinePadding.left - timelinePadding.right);
                                                    const statusColor = statusColors[task.status] || statusColors['open'];
                                                    
                                                    // Handle overlapping tasks by stacking vertically
                                                    const tasksOnSameDay = feature.tasks.filter(t => {
                                                        const tX = timelinePadding.left + (t.x / 100) * (timelineWidth - timelinePadding.left - timelinePadding.right);
                                                        return Math.abs(tX - x) < 5;
                                                    });
                                                    const sameDayIndex = tasksOnSameDay.findIndex(t => t.id === task.id);
                                                    const offsetY = (sameDayIndex - (tasksOnSameDay.length - 1) / 2) * 12;

                                                    return (
                                                        <circle
                                                            key={task.id}
                                                            cx={x}
                                                            cy={laneY + offsetY}
                                                            r="6"
                                                            fill={statusColor}
                                                            stroke="#ffffff"
                                                            strokeWidth="2"
                                                            className="task-marker"
                                                            onMouseEnter={(e) => handleTaskHover(task, e)}
                                                            onMouseLeave={handleTaskLeave}
                                                            onClick={() => handleTaskClick(task)}
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                    );
                                                })}
                                            </g>
                                        </g>
                                    );
                                })}

                                {/* Untagged Lane */}
                                {processedData.untagged.length > 0 && (() => {
                                    const untaggedIndex = processedData.features.length;
                                    const untaggedLaneY = timelinePadding.top + (untaggedIndex * laneHeight) + (laneHeight / 2);
                                    
                                    return (
                                    <g key="untagged" className="feature-lane untagged-lane">
                                        
                                        {/* Lane Background */}
                                        <rect
                                            x={timelinePadding.left}
                                            y={timelinePadding.top + (untaggedIndex * laneHeight)}
                                            width={timelineWidth - timelinePadding.left - timelinePadding.right}
                                            height={laneHeight}
                                            fill={untaggedIndex % 2 === 0 ? '#ffffff' : '#f9fafb'}
                                            className="lane-background"
                                            strokeDasharray="4,4"
                                            stroke="#d1d5db"
                                        />

                                        {/* Untagged Label */}
                                        <g className="lane-label">
                                            <rect
                                                x="0"
                                                y={timelinePadding.top + (untaggedIndex * laneHeight)}
                                                width={timelinePadding.left}
                                                height={laneHeight}
                                                fill="transparent"
                                            />
                                            <text
                                                x={timelinePadding.left - 10}
                                                y={untaggedLaneY}
                                                textAnchor="end"
                                                fontSize="12"
                                                fill="#6b7280"
                                                fontWeight="500"
                                                dominantBaseline="middle"
                                            >
                                                Untagged
                                            </text>
                                            <text
                                                x={timelinePadding.left - 10}
                                                y={untaggedLaneY + 18}
                                                textAnchor="end"
                                                fontSize="11"
                                                fill="#9ca3af"
                                                className="lane-task-count"
                                                dominantBaseline="middle"
                                            >
                                                ({processedData.untagged.length})
                                            </text>
                                        </g>

                                        {/* Untagged Task Markers */}
                                        <g className="task-markers">
                                            {processedData.untagged.map((task) => {
                                                const x = timelinePadding.left + (task.x / 100) * (timelineWidth - timelinePadding.left - timelinePadding.right);
                                                const statusColor = statusColors[task.status] || statusColors['open'];
                                                
                                                return (
                                                    <circle
                                                        key={task.id}
                                                        cx={x}
                                                        cy={untaggedLaneY}
                                                        r="6"
                                                        fill={statusColor}
                                                        stroke="#ffffff"
                                                        strokeWidth="2"
                                                        className="task-marker"
                                                        onMouseEnter={(e) => handleTaskHover(task, e)}
                                                        onMouseLeave={handleTaskLeave}
                                                        onClick={() => handleTaskClick(task)}
                                                        style={{ cursor: 'pointer' }}
                                                    />
                                                );
                                            })}
                                        </g>
                                    </g>
                                    );
                                })()}
                            </g>

                            {/* Time Axis */}
                            <g className="time-axis">
                                <line
                                    x1={timelinePadding.left}
                                    y1={timelineHeight - timelinePadding.bottom}
                                    x2={timelineWidth - timelinePadding.right}
                                    y2={timelineHeight - timelinePadding.bottom}
                                    stroke="#d1d5db"
                                    strokeWidth="2"
                                />
                                {timeAxisLabels.map((label, index) => {
                                    const x = timelinePadding.left + (label.x / 100) * (timelineWidth - timelinePadding.left - timelinePadding.right);
                                    return (
                                        <g key={`axis-${index}`}>
                                            <line
                                                x1={x}
                                                y1={timelineHeight - timelinePadding.bottom}
                                                x2={x}
                                                y2={timelineHeight - timelinePadding.bottom + 5}
                                                stroke="#d1d5db"
                                                strokeWidth="2"
                                            />
                                            <text
                                                x={x}
                                                y={timelineHeight - timelinePadding.bottom + 20}
                                                textAnchor="middle"
                                                fontSize="10"
                                                fill="#6b7280"
                                            >
                                                {label.label}
                                            </text>
                                        </g>
                                    );
                                })}
                            </g>
                        </svg>

                        {/* Tooltip */}
                        {hoveredTask && (
                            <div
                                className="timeline-tooltip"
                                style={{
                                    left: `${tooltipPosition.x}px`,
                                    top: `${tooltipPosition.y - 70}px`,
                                    transform: 'translateX(-50%)'
                                }}
                            >
                                <div className="tooltip-title">{hoveredTask.title}</div>
                                <div className="tooltip-details">
                                    <span className="tooltip-status" style={{ color: statusColors[hoveredTask.status] || statusColors['open'] }}>
                                        {hoveredTask.status.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                    </span>
                                    <span className="tooltip-date">{formatDate(hoveredTask.created_at)}</span>
                                    {hoveredTask.assigned_to && (
                                        <span className="tooltip-assignee">
                                            {hoveredTask.assigned_to.display_name || hoveredTask.assigned_to.email}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Summary Statistics */}
                {summary.totalTasks > 0 && (
                    <div className="timeline-summary">
                        <span>{summary.totalTasks} tasks</span>
                        <span>•</span>
                        <span>{summary.featureCount} features</span>
                        {summary.untaggedCount > 0 && (
                            <>
                                <span>•</span>
                                <span>{summary.untaggedCount} untagged</span>
                            </>
                        )}
                        <span>•</span>
                        <span>Span: {summary.spanDays} days</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TaskFeatureTimeline;

